import { FCAdditionalProps, getRem } from "@datassential/platform-ui-lib";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import Title from "@components/Title";
import cn from "classnames";
import { ProfileDataResponseItem } from "@core/api/useProfileHeader";
import { useCommonDataContext } from "@core/CommonDataContext";
import { ComponentProps, FC, useId, useMemo } from "react";
import {
    getMetricColor,
    TREND_METRICS_ORDER,
} from "@models/Metric";
import { SelectSingle } from "@components/Select";
import './Trend.scss';
import { compareQuarterYearDateStrings } from "@core/utils/date";
import { AiOutlineInfoCircle } from "react-icons/ai";
import HeatScoreLabel from "@vms/HeatScoreLabel";
import { useProfileContext } from "@pages/Profile/ProfileContext";
import { ProfileTrendRequest, useProfileTrend } from "@core/api/useProfileTrend";
import { useMetricOptions } from "@core/hooks/useMetricOptions";
import HeatScoreTooltip from "@vms/HeatScoreTooltip";
import GroupsColumnsLineChart from "@components/GroupsColumnsLineChart";
import { TREND_CHART_QUARTERS } from "@core/constants";
import { IOption } from "@models";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";

type TrendProps = Omit<FCAdditionalProps, 'id'> & {
    id: ProfileDataResponseItem["id"];
    heatScore: ProfileDataResponseItem['heatScore'];
    name?: string;
}

const Trend: FC<TrendProps> = ({
    id,
    className,
    name,
    heatScore,
    testId = "profileTrend",
    ...props
}) => {
    const tooltipId = useId();
    const {
        trendMetric,
        setTrendMetric,
    } = useProfileContext();

    const { amongPayload } = useSelectAmongContext();
    const payload = useMemo(
        (): ProfileTrendRequest => ({
            ...amongPayload,
            id,
        }),
        [amongPayload, id]
    );

    const {
        data: rawTrend,
        isLoading
    } = useProfileTrend(payload);

    const {
        metrics: metricsConfig,
        surveys,
    } = useCommonDataContext();

    const metricsOptions = useMetricOptions(TREND_METRICS_ORDER);

    const selectedMetric = useMemo(
        () => metricsConfig?.find((i) => i.id === trendMetric),
        [metricsConfig, trendMetric],
    );

    const selectedMetricData = useMemo(() => (rawTrend.find(i => i.metric === trendMetric)?.data ?? []).map((data) => {
        const survey = surveys?.find((survey) => survey.id === data.surveyId)
        return {
            ...data,
            surveyName: survey?.name ?? '',
        }
    }), [surveys, rawTrend, trendMetric])

    const chartYearQuarters = useMemo(() => {
        return selectedMetricData.sort((a, b) => {
            return compareQuarterYearDateStrings(a.surveyName, b.surveyName);
        });
    }, [selectedMetricData])

    const [chartData, chartYears] = useMemo(
        (): [ComponentProps<typeof GroupsColumnsLineChart>['series'], IOption[]] => {
            const chartYears: string[] = []
            const chartData = [{
                id,
                color: getMetricColor(trendMetric),
                title: name?.toUpperCase() ?? "",
                values: chartYearQuarters.map((q) => {
                    const [quarter, year] = q.surveyName.split(" ")
                    const groupId = year.toLowerCase()
                    const columnId = quarter.toLowerCase()
                    if (!chartYears.includes(groupId)) chartYears.push(groupId)

                    return {
                        groupId,
                        columnId,
                        value: q?.raw ?? null,
                    }
                })
            }]

            return [chartData, chartYears.map((year) => ({
                id: year,
                title: year,
            }))]
        },
        [trendMetric, chartYearQuarters, id, name],
    );

    if (!isLoading && !rawTrend.length) {
        return null;
    }

    return (
        <ContentWidget
            className={cn(
                "ProfileTrend",
                className,
                "appears-top", "del-4"
            )}
            isLoading={isLoading}
            testId={testId}
            {...props}
        >
            <div className="ContentWidget__header ProfileTrend__header">
                <Title testId="textProfileTrendTitle">TREND</Title>
                <div className="ProfileTrend__header-content">
                        <div className="ProfileTrend__header-filters">
                            <SelectSingle
                                value={trendMetric}
                                setValue={setTrendMetric}
                                options={metricsOptions}
                                label="SHOW:"
                                testId="selectProfileTrendMetric"
                            />
                        </div>
                        {!!heatScore && (
                            <div className="ProfileTrend__header-box">
                                <AiOutlineInfoCircle
                                    data-tooltip-id={tooltipId}
                                    className="ProfileTrend__info-icon"
                                    data-testid="blockProfileTrendInfoIcon"
                                />
                                <div className="ProfileTrend__header-box-label">Heat score:</div>
                                <HeatScoreLabel
                                    heatScoreId={heatScore}
                                    testId="textProfileTrendHeatScore"
                                />
                            </div>
                        )}
                        <HeatScoreTooltip id={tooltipId}/>
                </div>
            </div>
            <div className="ProfileTrend__content">
                {chartData.length > 0 && (
                    <GroupsColumnsLineChart
                        className="HistoricTrend__chart-container HistoricTrendChart"
                        series={chartData}
                        groups={chartYears}
                        columns={TREND_CHART_QUARTERS}
                        renderValue={(v) => typeof v === 'number' ? `${Math.round(v)}%` : '-'}
                        renderTooltipTitle={(groupTitle, columnTitle) => <>
                            {selectedMetric?.name?.toUpperCase()} <br/>
                            <span style={{ fontSize: getRem(12) }}>
                                YE {columnTitle} {groupTitle}
                            </span>
                        </>}
                        testId="blockProfileTrendChart"
                    />
                )}
            </div>
        </ContentWidget>
    );
};

export default Trend;